// node_modules
import React, { useContext, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import {useMobile} from '../../../hooks/useMobile'

// redux
import {
  useCreateUserMutation,
  useDeleteUserMutation,
  useGetUserListQuery, useUpdateUserMutation, useUpdateUserPasswordMutation
} from '../../../redux/services/user'
import { setLoading } from "../../../redux/slices/app.slice";

// primereact components
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputMask } from "primereact/inputmask";

// context
import { ToastContext } from "../../../App";

// utils
import getSubdomain from "../../../utils/getSubdomain";

// styles
import * as styles from "./ChefManagement.module.css";

const ChefManagement = () => {
  const showToast = useContext(ToastContext);
  const dispatch = useDispatch();
  const {isMobile} = useMobile();
  
  const { control, setValue, handleSubmit } = useForm({
    defaultValues: {
      username: "",
      role: "chef",
      email: "",
      phone: "",
    },
  });
  
  const {
    data: chefList,
    isSuccess: isGetChefListSuccess,
    isLoading: isGetChefListLoading,
    isError: isGetChefListError
  } = useGetUserListQuery(getSubdomain());
  const [createChef, { isSuccess: isCreateChefSuccess, isError: isCreateChefError, error: createChefError }] =
    useCreateUserMutation();
  const [deleteUser, {isSuccess: isDeleteChefSuccess, isError: isDeleteChefError, error: deleteChefError }]
    = useDeleteUserMutation();
  const [updateChef, { isSuccess: isUpdateChefSuccess, isError: isUpdateChefError, error: updateChefError }] =
    useUpdateUserMutation();
  const [updateChefPassword, { isSuccess: isUpdateChefPasswordSuccess, isError: isUpdateChefPasswordError, error: updateChefPasswordError }] =
    useUpdateUserPasswordMutation();
  
  const [id, setId] = useState(null);
  const [username, setUsername] = useState(null);
  const [mode, setMode] = useState("create");
  const [showModal, setShowModal] = useState(false);
  
  const openModal = (mode, rowData) => () => {
    if (mode === "create") {
      setId(null);
      setValue("username", "");
      setValue("email", "");
      setValue("phone", "");
      setValue("password", "");
    } else if(mode === "edit") {
      setId(rowData.id);
      setValue("username", rowData.username);
      setValue("email", rowData.email);
      setValue("phone", rowData.phone?.slice(2) || "");
    } else {
      setId(rowData.id)
      setValue("username", "");
      setValue("email", "");
      setValue("phone", "");
      setValue("password", "");
    }
    setShowModal(true);
    setMode(mode);
  };
  
  const onCreate = async (data) => {
    const { username, role, email, phone, password } = data;
    
    const payload = {
      email,
      password,
      username,
      phone: `+1${phone}`,
      userRole: role,
    };
    
    createChef({ subdomain: getSubdomain(), payload });
  };
  
  const onSaveEdit = async (data) => {
    const { username, email, phone } = data;
    
    const payload = {
      username,
      phone: `+1${phone}`,
      email,
    };
    updateChef({
      subdomain: getSubdomain(),
      id: id,
      payload,
    });
  };
  
  const onSavePassword = async (data) => {
    const { password } = data;
    
    const payload = {
      id,
      password,
    };
    updateChefPassword({
      subdomain: getSubdomain(),
      payload,
    });
  }
  
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (mode === "create") {
        handleSubmit(onCreate)();
      } else if (mode === "edit") {
        handleSubmit(onSaveEdit)();
      } else {
        handleSubmit(onSavePassword)();
      }
    }
  };
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  useEffect(() => {
    if(isGetChefListSuccess)
      dispatch(setLoading(false));
  }, [isGetChefListSuccess])
  
  useEffect(() => {
    if(isGetChefListError) {
      showToast('error', 'Chef', 'Unable to load chefs list')
      dispatch(setLoading(false));
    }
  }, [isGetChefListError])
  
  useEffect(() => {
    if(isCreateChefSuccess) {
      showToast('success', 'Chef', 'Chef created!')
      setShowModal(false);
    }
  }, [isCreateChefSuccess])
  
  useEffect(() => {
    if(isCreateChefError)
      showToast('error', createChefError?.status, createChefError?.data?.message || (createChefError?.data?.errors && "Incorrect data"))
  }, [isCreateChefError])
  
  useEffect(() => {
    if(isUpdateChefSuccess) {
      showToast('success', 'Chef', 'Chef updated!')
      setShowModal(false);
    }
  }, [isUpdateChefSuccess])
  
  useEffect(() => {
    if(isUpdateChefError) {
      showToast("error", updateChefError?.status, updateChefError?.data?.message || (updateChefError?.data?.errors && "Incorrect data"))
    }
  }, [isUpdateChefError])
  
  useEffect(() => {
    if(isDeleteChefSuccess) {
      showToast('success', 'Chef', 'Chef deleted!')
      setShowDeleteModal(false)
    }
  }, [isDeleteChefSuccess]);
  
  useEffect(() => {
    if(isDeleteChefError) {
      showToast('error', deleteChefError?.status, deleteChefError?.data?.message || (deleteChefError?.data?.errors && "Incorrect data"));
    }
  }, [isDeleteChefError])
  
  useEffect(() => {
    if(isUpdateChefPasswordSuccess) {
      showToast('success', 'Chef', 'Chef password updated!')
      setShowModal(false)
    }
  }, [isUpdateChefPasswordSuccess])
  
  useEffect(() => {
    if(isUpdateChefPasswordError) {
      showToast('error', updateChefPasswordError?.status, updateChefPasswordError?.data?.message || (updateChefPasswordError?.data?.errors && "Incorrect data"));
    }
  }, [isUpdateChefPasswordError])
  
  const openDeleteModal = (rowData) => () => {
    setId(rowData.id)
    setUsername(rowData.username)
    setShowDeleteModal(true)
  }
  
  const onDelete = () => {
    deleteUser({
      subdomain: getSubdomain(),
      id: id,
    })
  }
  
  const actionBodyTemplate = (rowData) => {
    return (
      <div className={styles.cellSoundWrapper}>
        <Button
          icon="pi pi-pencil"
          className={`p-button-text ${styles.soundBtn}`}
          onClick={openModal("edit", rowData)}
        />
        <Button
          icon="pi pi-key"
          className={`p-button-text ${styles.soundBtn}`}
          onClick={openModal("password", rowData)}
        />
        <Button
          icon="pi pi-trash"
          className={`p-button-text ${styles.deleteBtn}`}
          onClick={openDeleteModal(rowData)}
        />
      </div>
    );
  };
  
  return (
    <div className={styles.pageWrapper}>
      <div
        className={`${styles.header}`}
      >
        <h2>Chefs</h2>
        <Button className={styles.createBtn} onClick={openModal("create")}>
          <span>ADD CHEF</span> <i className="pi pi-plus" />
        </Button>
      </div>
      {chefList && (
        isMobile
          ? (
            <div className={styles.cardList}>
              {chefList.map((chef) => (
                <div key={chef.email} className={styles.cardItem}>
                  <div className={styles.cardInfo}>
                    <div className={styles.name}>{chef.username}</div>
                    <div className={styles.subfield}>{chef.email}</div>
                    <div className={styles.subfield}>{chef.phone}</div>
                  </div>
                  <div className={styles.actions}>
                    <button className={styles.cardEditBtn} onClick={openModal("edit", chef)}>
                      <i className="pi pi-pencil" />
                    </button>
                    <button className={styles.cardEditBtn} onClick={openModal("password", chef)}>
                      <i className="pi pi-key" />
                    </button>
                    <button className={styles.cardDeleteBtn} onClick={openDeleteModal(chef)}>
                      <i className="pi pi-trash" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
          : (
            <DataTable value={chefList} rows={6} loading={isGetChefListLoading}>
          <Column
            field="username"
            header="Username"
            body={(rowData) => {
              return <div className={styles.cellInsider}>{rowData.username}</div>;
            }}
          />
          
          <Column
            field="email"
            header="Email"
            body={(rowData) => {
              return <div className={styles.cellInsider}>{rowData.email}</div>;
            }}
          />
          
          <Column
            field="phone"
            header="Phone"
            body={(rowData) => {
              return <div className={styles.cellInsider}>{rowData.phone}</div>;
            }}
          />
          
          <Column
            body={actionBodyTemplate}
            header="Actions"
            style={{ width: "175px" }}
          />
        </DataTable>)
        )}
      
      <Dialog
        header={`${mode === "create" ? "New Chef" : mode === "edit" ? "Edit Chef" : "Edit Chef Password"}`}
        visible={showModal}
        onHide={() => {
          setShowModal(false);
        }}
        footer={() => (
          <div className="flex gap-2 justify-content-end">
            <Button
              label="Save"
              type="submit"
              className={`p-button-success ${styles.saveBtn}`}
              onClick={
                mode === "create"
                  ? handleSubmit(onCreate) : mode === "edit" ?
                  handleSubmit(onSaveEdit) : handleSubmit(onSavePassword)
              }
            />
          </div>
        )}
        className={`p-fluid ${styles.chefModal}`}
      >
        <form>
          {mode !== "password" && (
            <>
              <Controller
                name="username"
                control={control}
                render={({ field }) => (
                  <div className="field">
                    <label>Username</label>
                    <InputText
                      className={`w-full mb-3 ${styles.input}`}
                      onKeyDown={handleKeyDown}
                      placeholder="Username"
                      {...field}
                    />
                  </div>
                )}
              />
              
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <div className="field">
                    <label>Email</label>
                    <InputText
                      className={`w-full mb-3 ${styles.input}`}
                      onKeyDown={handleKeyDown}
                      placeholder="Email"
                      {...field}
                    />
                  </div>
                )}
              />
              
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: "Enter your phone number",
                  minLength: {
                    value: 10,
                    message: "Phone number is invalid",
                  },
                }}
                render={({ field, fieldState }) => (
                  <div className="field">
                    <label>Phone Number</label>
                    <div>
                      <div className="p-inputgroup flex-1">
                  <span className={`p-inputgroup-addon mb-3 ${styles.phonePrefix}`}>
                    +1
                  </span>
                        <InputMask
                          className={`w-full mb-3 ${styles.input} ${styles.inputPhone} ${
                            fieldState.invalid ? "p-invalid" : ""
                          }`}
                          placeholder="(999) 999-9999"
                          prefix="+1"
                          mask="(999) 999-9999"
                          unmask={true}
                          {...field}
                        />
                      </div>
                      
                      {fieldState.error && (
                        <small className={`p-error `}>
                          {fieldState.error.message}
                        </small>
                      )}
                    </div>
                  </div>
                )}
              />
            </>
          )}
          
          {mode !== "edit" && (
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <div className="field">
                  <label>Password</label>
                  <InputText
                    className={`w-full ${styles.input}`}
                    onKeyDown={handleKeyDown}
                    autoComplete="new-password"
                    type="password"
                    placeholder="Password"
                    {...field}
                  />
                </div>
              )}
            />
          )}
        </form>
      </Dialog>
      
      <Dialog
        closable={false}
        header={
          <div style={{ width: '100%', textAlign: 'center' }}>
            Are You Sure?
          </div>}
        visible={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
        }}
        footer={() => (
          <div className="flex flex-column gap-2 justify-content-end">
            <Button
              label="Delete"
              className={`p-button-success ${styles.deleteDialogBtn}`}
              onClick={onDelete}
            />
            <Button
              label="Cancel"
              text
              className={`p-button-success ${styles.cancelDialogBtn}`}
              onClick={() => {setShowDeleteModal(false)}}
            />
          </div>
        )}
        className={`p-fluid ${styles.deleteModal}`}
      >
        <div className={styles.text}>
          <span>You want to delete the chef: <strong>{username}</strong>.</span>
          Confirm this action by pressing ‘Delete’ or cancel it.
        </div>
      </Dialog>
    </div>
  );
};

export default ChefManagement;
